import { instance } from "api/apiBase";

/** A partner-defined Lead tag (CRM-1599). Names are unique ignoring capitalization. */
export interface LeadTag {
  id: number;
  name: string;
  /** Live leads carrying the tag; only returned to admins who ask for counts. */
  leads_count?: number;
}

/** What adding a tag to, or removing it from, a Lead returns. */
export interface LeadTagChangeResponse {
  tag: LeadTag;
  /** The Lead's tags after the change, A to Z. */
  tags: LeadTag[];
}

/** A workflow whose trigger, Filter or Branch checks a tag (CRM-1684). */
export interface TagWorkflowUsage {
  id: number;
  name: string;
  enabled: boolean;
  /** The published version checks the tag. */
  live: boolean;
  /** The draft checks the tag. */
  draft: boolean;
}

export const LEAD_TAGS_QUERY_KEY = "LEAD_TAGS";
export const LEAD_TAG_NAME_MAX_LENGTH = 30;
/** Mirrors Boom::Tag::NAME_PATTERN: letters, digits, spaces, punctuation, a few symbols. */
export const LEAD_TAG_NAME_PATTERN = /^[\p{L}\p{M}\p{N}\p{P} +$=<>~^|]+$/u;
export const LEAD_TAGS_PER_LEAD_LIMIT = 20;

const BASE_URL = "/portal/v1/tags";

/** Mirrors Boom::Tag.normalize_name so the client matches names the way the API does. */
export const normalizeLeadTagName = (raw: string): string =>
  raw.normalize("NFC").replace(/\s+/g, " ").trim();

export const isSameLeadTagName = (a: string, b: string): boolean =>
  normalizeLeadTagName(a).toLowerCase() ===
  normalizeLeadTagName(b).toLowerCase();

export const compareLeadTagNames = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

export const sortLeadTags = <T extends { name: string }>(tags: T[]): T[] =>
  tags.toSorted((a, b) => compareLeadTagNames(a.name, b.name));

export const leadTagsApi = {
  list: async ({
    includeCounts = false
  }: { includeCounts?: boolean } = {}): Promise<LeadTag[]> => {
    const { data } = await instance.get<{ items: LeadTag[] }>(BASE_URL, {
      params: includeCounts ? { include_counts: true } : undefined
    });
    return data.items;
  },

  create: async (name: string): Promise<LeadTag> => {
    const { data } = await instance.post<LeadTag>(BASE_URL, { name });
    return data;
  },

  rename: async ({ id, name }: { id: number; name: string }) => {
    const { data } = await instance.put<LeadTag>(`${BASE_URL}/${id}`, {
      name
    });
    return data;
  },

  remove: async (id: number): Promise<void> => {
    await instance.delete(`${BASE_URL}/${id}`);
  },

  /** Workflows that check the tag; admins only. */
  workflowUsages: async (id: number): Promise<TagWorkflowUsage[]> => {
    const { data } = await instance.get<{ items: TagWorkflowUsage[] }>(
      `${BASE_URL}/${id}/workflow_usages`
    );
    return data.items;
  },

  /** Adds an existing tag by id, or by name; a new name creates the tag (admins only). */
  addToLead: async ({
    leadId,
    tagId,
    name
  }: {
    leadId: number;
    tagId?: number;
    name?: string;
  }): Promise<LeadTagChangeResponse> => {
    const { data } = await instance.post<LeadTagChangeResponse>(
      `/portal/v1/leads/${leadId}/tags`,
      tagId ? { tag_id: tagId } : { name }
    );
    return data;
  },

  removeFromLead: async ({
    leadId,
    tagId
  }: {
    leadId: number;
    tagId: number;
  }): Promise<LeadTagChangeResponse> => {
    const { data } = await instance.delete<LeadTagChangeResponse>(
      `/portal/v1/leads/${leadId}/tags/${tagId}`
    );
    return data;
  }
};
